import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './slideshow.module.css';

// Each slide can carry both a small `thumbnail` (~800 px WebP) for fast
// first-paint and a full-size `image` for fullscreen / pinch-zoom. Older API
// shapes might give us just a string URL or only one of the two — handle both.
const extractSlide = item => {
	if (!item) return null;
	if (typeof item === 'string') return { thumbnail: item, image: item };
	const image = item.image || item.url || item.src || null;
	const thumbnail = item.thumbnail || image;
	if (!image && !thumbnail) return null;
	return { thumbnail: thumbnail || image, image: image || thumbnail };
};

const SWIPE_THRESHOLD = 50;
const PRELOAD_RANGE = 3;

export default function Slideshow({ story }) {
	const slides = useMemo(() => {
		const fromGallery = (story?.gallery || []).map(extractSlide).filter(Boolean);
		if (fromGallery.length) return fromGallery;
		const fromItems = (story?.items || []).map(extractSlide).filter(Boolean);
		if (fromItems.length) return fromItems;
		if (story?.image) {
			return [{ thumbnail: story.thumbnail || story.image, image: story.image }];
		}
		return [];
	}, [story]);

	const [idx, setIdx] = useState(0);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [imageReady, setImageReady] = useState(false);
	// Whether the *full* image (not just the thumbnail) is in the browser
	// cache for the current slide. When this flips true we swap src to the
	// full original — instant since it's cached.
	const [hiResReady, setHiResReady] = useState(false);
	const imgRef = useRef(null);
	const touchStartX = useRef(null);
	const touchStartY = useRef(null);

	// Reset readiness whenever the visible slide changes; if the thumbnail
	// is already cached (browser fired its load before React attached the
	// handler), pick that up immediately on the next render via the ref.
	useEffect(() => {
		setImageReady(false);
		setHiResReady(false);
		if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
			setImageReady(true);
		}
	}, [idx, slides]);

	// Preload the full image of the current slide in the background;
	// swap the visible <img> src to it as soon as it's cached.
	useEffect(() => {
		if (!slides.length) return undefined;
		const cur = slides[idx];
		if (!cur || cur.image === cur.thumbnail) {
			setHiResReady(true);
			return undefined;
		}
		let cancelled = false;
		const pre = new Image();
		const settle = () => {
			if (!cancelled) setHiResReady(true);
		};
		pre.onload = settle;
		pre.onerror = settle;
		pre.src = cur.image;
		return () => {
			cancelled = true;
		};
	}, [idx, slides]);

	// Lock body scroll while in fullscreen
	useEffect(() => {
		if (!isFullscreen) return undefined;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isFullscreen]);

	// Keyboard shortcuts in fullscreen
	useEffect(() => {
		if (!isFullscreen) return undefined;
		const onKey = e => {
			if (e.key === 'Escape') setIsFullscreen(false);
			else if (e.key === 'ArrowLeft') setIdx(i => (i - 1 + slides.length) % slides.length);
			else if (e.key === 'ArrowRight') setIdx(i => (i + 1) % slides.length);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [isFullscreen, slides.length]);

	// Preload neighbouring slides — both the thumbnail (for instant first
	// paint of the next slide) and the full image (for the in-place upgrade
	// once the user lands on that slide).
	useEffect(() => {
		if (slides.length <= 1) return;
		const seen = new Set();
		for (let step = 1; step <= PRELOAD_RANGE; step += 1) {
			const next = (idx + step) % slides.length;
			const prev = (idx - step + slides.length) % slides.length;
			[next, prev].forEach(i => {
				if (seen.has(i) || i === idx) return;
				seen.add(i);
				const slide = slides[i];
				const tImg = new Image();
				tImg.src = slide.thumbnail;
				if (slide.image !== slide.thumbnail) {
					const fImg = new Image();
					fImg.src = slide.image;
				}
			});
		}
	}, [idx, slides]);

	if (!slides.length) return null;

	const multiple = slides.length > 1;
	const go = step => setIdx(i => (i + step + slides.length) % slides.length);

	// Thumbnail first (~50 KB, paints fast); upgrade to the full original as
	// soon as it's preloaded. Browser uses the cached version, so the swap
	// is seamless. Fullscreen always shows the full version.
	const currentSrc = isFullscreen || hiResReady ? slides[idx].image : slides[idx].thumbnail;

	const handleImageClick = e => {
		if (!multiple) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		go(clickX < rect.width / 2 ? -1 : 1);
	};

	const handleTouchStart = e => {
		if (!multiple) return;
		const t = e.touches[0];
		touchStartX.current = t.clientX;
		touchStartY.current = t.clientY;
	};

	const handleTouchEnd = e => {
		if (!multiple || touchStartX.current === null) return;
		const t = e.changedTouches[0];
		const dx = t.clientX - touchStartX.current;
		const dy = t.clientY - touchStartY.current;
		// Only trigger swipe if mostly horizontal and over the threshold
		if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
			go(dx > 0 ? -1 : 1);
			// Cancel the synthetic click that follows touchend
			e.preventDefault();
		}
		touchStartX.current = null;
		touchStartY.current = null;
	};

	return (
		<div className={`${styles.container}${isFullscreen ? ` ${styles.containerFullscreen}` : ''}`}>
			<div className={styles.imageWrap}>
				<img
					ref={imgRef}
					src={currentSrc}
					alt={`slide ${idx + 1}`}
					className={styles.image}
					style={{ cursor: multiple ? 'pointer' : 'default' }}
					onClick={handleImageClick}
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
					onLoad={() => setImageReady(true)}
					onError={() => setImageReady(true)}
				/>
				{imageReady && (
					<>
						<button
							type="button"
							className={`${styles.toggleFs}${isFullscreen ? ` ${styles.closeFs}` : ''}`}
							onClick={() => setIsFullscreen(v => !v)}
							aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
						>
							{isFullscreen ? '×' : '☐'}
						</button>
						{multiple && (
							<>
								<button
									type="button"
									className={`${styles.button} ${styles.buttonLeft}`}
									onClick={() => go(-1)}
									aria-label="Previous"
								>
									‹
								</button>
								<button
									type="button"
									className={`${styles.button} ${styles.buttonRight}`}
									onClick={() => go(1)}
									aria-label="Next"
								>
									›
								</button>
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
}
